import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Send, CheckCircle2, Headphones, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/support/contact`, form, { withCredentials: true });
      setSent(true);
      toast.success(t("support.messageSent"));
      setForm({ email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(t("support.sendError"));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] animate-in fade-in duration-500" data-testid="support-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{t("support.thankYou")}</h2>
          <p className="text-muted-foreground mb-6">{t("support.thankYouDesc")}</p>
          <Button onClick={() => setSent(false)} variant="outline" className="rounded-xl" data-testid="send-another-btn">
            {t("support.sendAnother")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in duration-500" data-testid="support-page">
      <div>
        <h1 className="text-2xl font-bold">{t("support.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("support.subtitle")}</p>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
            <Mail className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">{t("support.emailUs")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">support.ellalink@gmail.com</p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
            <Headphones className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">{t("support.responseTime")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("support.responseTimeDesc")}</p>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            {t("support.contactForm")}
          </CardTitle>
          <CardDescription>{t("support.contactFormDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t("support.yourEmail")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-11 rounded-xl"
                required
                data-testid="support-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-sm font-medium">{t("support.subject")}</Label>
              <Input
                id="subject"
                type="text"
                placeholder={t("support.subjectPlaceholder")}
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="h-11 rounded-xl"
                required
                data-testid="support-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium">{t("support.message")}</Label>
              <Textarea
                id="message"
                placeholder={t("support.messagePlaceholder")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                className="rounded-xl resize-none"
                required
                data-testid="support-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
              data-testid="support-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("support.sending")}
                </span>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t("support.send")}
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
